import * as React from 'react'
import { Link, NavLink, Outlet } from 'react-router'
import { LogOut, Menu, Sprout } from 'lucide-react'
import { useAuth } from '@/auth/AuthContext'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import { cn } from '@/lib/utils'
import { useOrganizationBranding } from '@/hooks/useOrganization'

interface NavItem {
  to: string
  label: string
}

const NAV_ITEMS: NavItem[] = [
  { to: '/clients', label: 'Clients' },
  { to: '/proposals', label: 'Proposals' },
  { to: '/team', label: 'Team' },
  { to: '/settings', label: 'Settings' },
]

/**
 * Active-aware class for a nav link. The active state is carried by text color
 * and weight rather than a filled pill — chrome stays neutral, per the design
 * system's first principle.
 */
function navLinkClass({ isActive }: { isActive: boolean }): string {
  return cn(
    'rounded-md px-2 py-1 text-sm transition-colors hover:bg-subtle',
    isActive
      ? 'font-medium text-primary-text'
      : 'text-secondary-text hover:text-primary-text',
  )
}

/** The same link, sized for a thumb rather than a cursor. */
function drawerLinkClass({ isActive }: { isActive: boolean }): string {
  return cn(
    'flex min-h-11 items-center rounded-md px-3 py-1.5 text-sm transition-colors',
    isActive
      ? 'bg-brand-subtle font-medium text-primary-text'
      : 'text-secondary-text hover:bg-subtle hover:text-primary-text',
  )
}

/**
 * Navigation below the desktop breakpoint.
 *
 * A drawer, not a collapsed icon rail — the design system's responsive rules
 * fix the breakpoint at `lg` and rule out a tablet rail, so there is one
 * behavior to learn rather than three.
 *
 * Built on the existing `Dialog` rather than hand-rolled: a drawer needs a
 * focus trap, Escape to close, scroll lock behind it and the right ARIA, and
 * every one of those is a thing to get subtly wrong twice.
 */
function NavDrawer({
  organizationName,
  email,
  onSignOut,
}: {
  organizationName: string
  email: string | null
  onSignOut: () => void
}) {
  const [open, setOpen] = React.useState(false)

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="h-9 w-9 lg:hidden"
          aria-label="Open navigation"
        >
          <Menu className="h-4 w-4" />
        </Button>
      </DialogTrigger>
      <DialogContent
        className={cn(
          // Override the primitive's centering: a drawer is pinned to the left
          // edge and full height. `cn` resolves the conflicting utilities in
          // favor of these.
          'top-0 left-0 h-svh w-72 max-w-[85vw] translate-x-0 translate-y-0',
          'grid-rows-[auto_1fr_auto] gap-0 rounded-none border-y-0 border-l-0 p-0',
        )}
        aria-describedby={undefined}
      >
        <div className="flex h-12 items-center gap-2 border-b border-default px-3">
          <Sprout className="h-4 w-4 text-brand" />
          <DialogTitle className="text-sm font-semibold">
            {organizationName}
          </DialogTitle>
        </div>

        <nav className="space-y-1 p-2">
          {NAV_ITEMS.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              className={drawerLinkClass}
              // Navigating is the drawer's whole purpose, so arriving somewhere
              // closes it. Leaving it open would cover the page just reached.
              onClick={() => {
                setOpen(false)
              }}
            >
              {item.label}
            </NavLink>
          ))}
        </nav>

        <div className="space-y-2 border-t border-default p-3">
          {/* The signed-in address is the way into the account page here too,
              so the route is found in the same place on both layouts. */}
          <NavLink
            to="/account"
            className={drawerLinkClass}
            onClick={() => {
              setOpen(false)
            }}
          >
            <span className="flex min-w-0 flex-col">
              <span>Your account</span>
              {email === null ? null : (
                <span className="truncate text-xs text-muted-text">{email}</span>
              )}
            </span>
          </NavLink>
          <Button
            variant="outline"
            className="h-11 w-full justify-start"
            onClick={onSignOut}
          >
            <LogOut className="h-4 w-4" />
            Sign out
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}

/**
 * The authenticated app shell: a compact top bar with the product mark,
 * navigation, the current user, and a sign-out action, above an `<Outlet>` for
 * the active route.
 *
 * The bar is 48px rather than the more common 64px: this is a data-first app,
 * and vertical space spent on chrome is space not spent on rows.
 *
 * Below `lg` the links, the signed-in address and sign-out all move into a
 * drawer. Laid out in a row they were about 90px wider than a phone, which
 * pushed the sign-out button off-screen and — because the overflow was on the
 * document rather than inside a scroller — let every page in the app slide
 * sideways under the thumb.
 */
export function AppLayout() {
  const { user, logout } = useAuth()
  const organization = useOrganizationBranding()
  /*
   * The stored name once it arrives, and nothing before it. An empty string
   * while the query is in flight rather than a placeholder that flashes
   * somebody else's business name on every cold load.
   */
  const organizationName = organization.data?.name ?? ''

  return (
    <div className="min-h-svh bg-app">
      <header className="sticky top-0 z-10 border-b border-default bg-surface">
        <div className="mx-auto flex h-12 max-w-7xl items-center justify-between gap-2 px-2 sm:px-4">
          <div className="flex min-w-0 items-center gap-1 lg:gap-6">
            <NavDrawer
              organizationName={organizationName}
              email={user?.email ?? null}
              onSignOut={logout}
            />
            <Link
              to="/"
              className="flex min-w-0 items-center gap-2 font-semibold whitespace-nowrap"
            >
              <Sprout className="h-4 w-4 shrink-0 text-brand" />
              <span className="truncate">{organizationName}</span>
            </Link>
            <nav className="hidden items-center gap-1 lg:flex">
              {NAV_ITEMS.map((item) => (
                <NavLink key={item.to} to={item.to} className={navLinkClass}>
                  {item.label}
                </NavLink>
              ))}
            </nav>
          </div>
          <div className="hidden shrink-0 items-center gap-3 lg:flex">
            {/* The address was already the only mark of who is signed in, so it
                is the natural handle for that person's own settings — quieter
                than a fifth nav item for a page visited about twice a year. */}
            {user ? (
              <NavLink
                to="/account"
                className={({ isActive }) =>
                  cn(
                    'max-w-[16rem] truncate rounded-md px-2 py-1 text-sm transition-colors hover:bg-subtle hover:text-primary-text',
                    isActive ? 'text-primary-text' : 'text-muted-text',
                  )
                }
              >
                {user.email}
              </NavLink>
            ) : null}
            <Button variant="ghost" size="sm" onClick={logout}>
              <LogOut className="h-3.5 w-3.5" />
              Sign out
            </Button>
          </div>
        </div>
      </header>
      <main className="mx-auto max-w-7xl px-3 py-4 sm:px-4 sm:py-6">
        <Outlet />
      </main>
    </div>
  )
}
